import { useEffect, useState } from 'react';
import DarwinTypography from 'components/darwin-ui/Typography';
import {
  ActionsDropdown,
  DarwinButton,
  DarwinCol,
  DarwinFlex,
  DarwinImage,
  DarwinRow,
  DarwinSpin,
} from 'components/darwin-ui';
import Icon from 'components/darwin-ui/Icon';
import styles from './AddressDetails.module.css';
import { CreateLocationPayload, Location } from 'types/organizationTypes';
import { useTranslation } from 'react-i18next';
import { useCreateOrganizationFormContext } from 'contexts/CreateOrganizationFormContext';
import { GenericDeleteModal } from 'components/ui/GenericDeleteModal';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { featureFlagsNames } from 'utils/featureFlag';
import DarwinGoogleMap from 'components/google-map/googleMap';
import { GeolocationModal } from '../GeolocationModal';
import { LatLng } from 'types/maps';
import { useGetCityCenter } from 'api/services/mapsPolygonService';
import { cityToCountryCode } from 'utils/maps';

const { Title, Text } = DarwinTypography;

const getSafeUrl = (url: string): string => {
  if (!/^https?:\/\//i.test(url)) {
    return `https://${url}`;
  }
  return url;
};

const center: LatLng = { lat: 49.260833333333, lng: -123.11388888889 };

interface LocationDetailsProps {
  location: Location;
  handleUpdateDisplayLocationForm: (updateDisplayLocationForm: boolean) => void;
  handleResetLocationFormValues: (data: CreateLocationPayload) => void;
  handleDeleteLocation: () => void;
  targetCompanyId: string;
  addressId: string;
  cityByAddress: string;
}

export const LocationDetails = ({
  location,
  handleUpdateDisplayLocationForm,
  handleResetLocationFormValues,
  handleDeleteLocation,
  targetCompanyId,
  addressId,
  cityByAddress,
}: LocationDetailsProps) => {
  const { t } = useTranslation();
  const { setIsEditingLocation } = useCreateOrganizationFormContext();
  const [displayDeleteModal, setDisplayDeleteModal] = useState<boolean>(false);
  const [viewingMap, setViewingMap] = useState<boolean>(false);
  const eudrGeolocation = useFeatureFlag(featureFlagsNames.eudrGeolocation);
  const [displayGeolocationModal, setDisplayGeolocationModal] =
    useState<boolean>(false);

  const handleGeolocationModal = () => {
    setDisplayGeolocationModal(!displayGeolocationModal);
  };

  const [countryCode, setCountryCode] = useState<string | null>(null);
  const [center, setCenter] = useState<LatLng>({ lat: 0, lng: 0 });

  const { data: centerData, isLoading: centerDataLoading } = useGetCityCenter(
    cityByAddress,
    {
      countryCode: countryCode || '',
    }
  );

  useEffect(() => {
    const city = cityByAddress?.trim();

    if (!city) {
      setCountryCode(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const code = await cityToCountryCode(city);
        if (!cancelled) setCountryCode(code);
      } catch {
        if (!cancelled) setCountryCode(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cityByAddress]);

  useEffect(() => {
    if (centerData) {
      setCenter({ lat: centerData.lat, lng: centerData.lng });
    }
  }, [centerData]);

  const textMap = !viewingMap
    ? t('create-organization-form.map-view')
    : t('create-organization-form.map-view-close');

  const { id: locationID } = location;

  const menuItems = () => [
    {
      key: 'edit',
      label: (
        <DarwinFlex
          gap={8}
          align="center"
          onClick={() => {
            setIsEditingLocation(true);
            const { name, description, process } = location;
            const editPayload: CreateLocationPayload = {
              name,
              description,
              processId: process.id,
              externalId: location.externalId || '',
            };

            if (!eudrGeolocation) {
              editPayload.externalUrl = location.externalUrl;
            }
            handleUpdateDisplayLocationForm(true);
            handleResetLocationFormValues(editPayload);
          }}
        >
          <Icon name={'edit'} size="sm" color="inherit" />
          Edit
        </DarwinFlex>
      ),
    },
    {
      key: 'delete',
      label: (
        <DarwinFlex
          gap={8}
          align="center"
          onClick={() => {
            setDisplayDeleteModal(true);
          }}
        >
          <Icon name={'delete'} size="sm" color="inherit" />
          Delete
        </DarwinFlex>
      ),
    },
  ];

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <Title level={5} className={styles.addressName}>
            {location.name}
          </Title>
          <ActionsDropdown getMenuItems={menuItems} />
        </div>
        <DarwinRow gutter={16}>
          <DarwinCol className={styles.dividerBorderTop} span={24}>
            <Text className={styles.label}>
              {t('create-organization-form.process-type')}
            </Text>
            <div className={`${styles.value}`}>{location.process.name}</div>
          </DarwinCol>
          <DarwinCol className={styles.dividerBorderTop} span={24}>
            <Text className={styles.label}>
              {t('create-organization-form.description')}
            </Text>
            <div className={styles.value}>{location.description}</div>
          </DarwinCol>
        </DarwinRow>
      </div>
      {!eudrGeolocation && (
        <DarwinFlex vertical>
          <DarwinFlex
            style={{ marginTop: '16px', justifyContent: 'space-between' }}
          >
            {location.externalUrl && (
              <DarwinButton
                type="primary"
                href={getSafeUrl(location.externalUrl)}
                target="_blank"
              >
                {t('create-organization-form.map-configuration')}
              </DarwinButton>
            )}
            {location.fileUrl && (
              <DarwinButton
                type="primary"
                onClick={() => setViewingMap(!viewingMap)}
              >
                {textMap}
              </DarwinButton>
            )}
          </DarwinFlex>
          {viewingMap && location.fileUrl && (
            <DarwinFlex style={{ marginTop: '16px' }}>
              <DarwinImage
                width={'100%'}
                height={300}
                src={location.fileUrl}
                preview={false}
              />
            </DarwinFlex>
          )}
        </DarwinFlex>
      )}

      {eudrGeolocation && (
        <div className={styles.mapContainer}>
          <Title
            level={5}
            className={styles.addressName}
            style={{ fontWeight: 600 }}
          >
            {t('geolocation.title')}
          </Title>
          <Text className={`${styles.value}`}>{t('geolocation.subtitle')}</Text>
          <DarwinFlex vertical style={{ width: '100%' }}>
            <DarwinSpin spinning={centerDataLoading} />
            {!centerDataLoading && (
              <>
                <DarwinGoogleMap
                  height={200}
                  disableUi={true}
                  mapType="satellite"
                  zoom={8}
                  center={center}
                />
                <DarwinFlex style={{ justifyContent: 'flex-end' }}>
                  <DarwinButton
                    className={styles.buttonMap}
                    style={{ width: 180 }}
                    onClick={handleGeolocationModal}
                  >
                    <Icon name="edit" size="sm"></Icon>
                    {t('geolocation.edit-button')}
                  </DarwinButton>
                </DarwinFlex>
              </>
            )}
          </DarwinFlex>
        </div>
      )}

      {displayGeolocationModal && (
        <GeolocationModal
          geolocationModalOpen={displayGeolocationModal}
          handleCloseModal={handleGeolocationModal}
          addressId={addressId}
          locationId={locationID}
          targetCompanyId={targetCompanyId}
          location={location}
          center={center}
        />
      )}

      <GenericDeleteModal
        open={displayDeleteModal}
        onCancel={() => {
          setDisplayDeleteModal(false);
        }}
        onOk={() => {
          handleDeleteLocation();
          setDisplayDeleteModal(false);
        }}
        bodyText={t('create-organization-form.delete-modal-body', {
          fieldName: 'ubicación',
        })}
      />
    </>
  );
};
